import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Buildings, Plus, Pencil, Trash, Globe, Phone, EnvelopeSimple, MapPin, X } from "@phosphor-icons/react";

export default function Companies() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | company-object

  const refresh = () => {
    setLoading(true);
    api.get("/companies").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  const canEdit = me?.role === "admin" || me?.role === "manager";

  const onArchive = async (c) => {
    if (!window.confirm(`Archive ${c.name}? You can unarchive later.`)) return;
    try {
      await api.delete(`/companies/${c.id}`);
      toast.success("Archived");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Archive failed");
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="font-mono-sms text-xs uppercase tracking-[0.3em] text-[var(--sms-primary)] mb-3">Partners</div>
          <h1 className="font-display text-5xl font-black tracking-tighter">Placement Companies.</h1>
          <p className="text-slate-500 mt-2">{items.length} partner{items.length === 1 ? "" : "s"} where students complete their placement.</p>
        </div>
        {canEdit && (
          <button onClick={() => setEditing("new")} data-testid="btn-create-company" className="sms-btn-primary inline-flex items-center gap-2">
            <Plus size={16} weight="bold" /> Add company
          </button>
        )}
      </div>

      {loading && <div className="text-sm text-slate-400 font-mono-sms uppercase tracking-[0.2em]">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="border border-dashed border-slate-300 p-16 text-center">
          <Buildings size={32} weight="duotone" className="text-slate-300 mx-auto mb-4" />
          <div className="font-display text-xl font-bold text-slate-700">No partners yet</div>
          <div className="text-sm text-slate-500 mt-1">Add your first placement partner to get started.</div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="companies-grid">
        {items.map((c) => (
          <div key={c.id} data-testid={`company-${c.id}`} className="border border-slate-200 p-5 flex flex-col gap-4 hover:border-slate-900 transition-colors">
            <div className="flex items-start gap-3">
              {c.logo_base64 ? (
                <img src={c.logo_base64} alt={c.name} className="w-14 h-14 object-contain border border-slate-200 bg-white p-1 shrink-0" />
              ) : (
                <div className="w-14 h-14 border border-slate-200 bg-slate-50 flex items-center justify-center shrink-0">
                  <Buildings size={22} weight="duotone" className="text-slate-300" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg font-black tracking-tight truncate">{c.name}</div>
                {c.website && (
                  <a href={c.website} target="_blank" rel="noopener noreferrer" className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-[var(--sms-primary)] hover:underline inline-flex items-center gap-1">
                    <Globe size={10} weight="bold" /> Website
                  </a>
                )}
              </div>
            </div>
            <div className="space-y-1 text-xs text-slate-600">
              {c.address && <div className="flex items-start gap-1.5"><MapPin size={12} weight="duotone" className="mt-0.5 shrink-0" /> <span className="break-words">{c.address}</span></div>}
              {c.phone && <div className="flex items-center gap-1.5"><Phone size={12} weight="duotone" /> <span>{c.phone}</span></div>}
              {c.email && <div className="flex items-center gap-1.5"><EnvelopeSimple size={12} weight="duotone" /> <span>{c.email}</span></div>}
            </div>
            {canEdit && (
              <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                <button onClick={() => setEditing(c)} data-testid={`edit-${c.id}`} className="sms-btn-secondary text-xs inline-flex items-center gap-1"><Pencil size={11} weight="bold" /> Edit</button>
                <button onClick={() => onArchive(c)} data-testid={`archive-${c.id}`} className="sms-btn-secondary text-xs inline-flex items-center gap-1 hover:text-[var(--sms-danger)]"><Trash size={11} weight="bold" /> Archive</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <CompanyModal
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { refresh(); setEditing(null); }}
        />
      )}
    </AdminLayout>
  );
}

function CompanyModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || { name: "", address: "", phone: "", email: "", website: "", logo_base64: null });
  const [busy, setBusy] = useState(false);

  const onLogo = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Logo must be an image"); return; }
    if (file.size > 4 * 1024 * 1024) { toast.error("Logo under 4 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo_base64: reader.result }));
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (initial) await api.patch(`/companies/${initial.id}`, form);
      else await api.post("/companies", form);
      toast.success(initial ? "Updated" : "Company added");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xl" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white border border-slate-900 shadow-2xl w-full max-w-lg p-6" data-testid="company-modal">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-[var(--sms-primary)]">{initial ? "Edit company" : "New company"}</div>
            <div className="font-display text-2xl font-black tracking-tighter mt-1">{initial ? initial.name : "Add a placement partner"}</div>
          </div>
          <button type="button" onClick={onClose}><X size={18} weight="bold" /></button>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-1">
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Logo</div>
            <div className="aspect-square border border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden">
              {form.logo_base64 ? <img src={form.logo_base64} alt="logo" className="max-w-full max-h-full" /> : <Buildings size={28} weight="duotone" className="text-slate-300" />}
            </div>
            <input type="file" accept="image/*" data-testid="company-logo-input" onChange={(e) => onLogo(e.target.files?.[0])} className="hidden" id="company-logo-file" />
            <label htmlFor="company-logo-file" className="sms-btn-secondary w-full mt-2 text-xs cursor-pointer block text-center">{form.logo_base64 ? "Replace" : "Upload"}</label>
          </div>
          <div className="col-span-2 space-y-3">
            <Field label="Company name *" data-testid="company-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
            <Field label="Address" data-testid="company-address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} multiline />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone" data-testid="company-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
              <Field label="Email" data-testid="company-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            </div>
            <Field label="Website" data-testid="company-website" value={form.website} onChange={(v) => setForm({ ...form, website: v })} placeholder="https://" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-5 mt-5 border-t border-slate-200">
          <button type="button" onClick={onClose} className="sms-btn-secondary text-xs">Cancel</button>
          <button type="submit" disabled={busy} data-testid="company-save" className="sms-btn-primary">{busy ? "Saving…" : initial ? "Save" : "Create"}</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, multiline, type = "text", placeholder, required, "data-testid": testid }) {
  return (
    <div>
      <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">{label}</div>
      {multiline ? (
        <textarea rows={2} className="sms-input" value={value} required={required} placeholder={placeholder} data-testid={testid} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input type={type} className="sms-input" value={value} required={required} placeholder={placeholder} data-testid={testid} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}
